import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useVehicles } from '../../hooks/useVehicles';

export interface InventorySearchInputProps {
  value: string;
  onChange: (val: string) => void;
  onSearch?: () => void;
  placeholder?: string;
  /** Show the yellow search button attached to the right of the input */
  showButton?: boolean;
  /** sm = compact (inventory toolbar), md = full size (hero card) */
  size?: 'sm' | 'md';
  className?: string;
  inputStyle?: React.CSSProperties;
  inputClassName?: string;
  /** Override bg/border styles on the outer input wrapper */
  wrapperStyle?: React.CSSProperties;
}

type Suggestion = { label: string; type: 'make' | 'model' | 'vehicle' };

export default function InventorySearchInput({
  value,
  onChange,
  onSearch,
  placeholder = 'Search make, model, year…',
  showButton = true,
  size = 'md',
  className = '',
  inputStyle,
  inputClassName = '',
  wrapperStyle,
}: InventorySearchInputProps) {
  const { vehicles } = useVehicles();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo<Suggestion[]>(() => {
    const q = value.trim().toLowerCase();
    if (!q || vehicles.length === 0) return [];
    const seen = new Set<string>();
    const results: Suggestion[] = [];

    vehicles.forEach(v => {
      const make = (v.make ?? '').trim();
      const model = (v.model ?? '').trim();
      const year = String(v.year ?? '');
      const trim = (v.trim ?? '').trim();

      const makeLabel = make;
      const makeModelLabel = `${make} ${model}`.trim();
      const fullLabel = `${year} ${make} ${model}${trim ? ' ' + trim : ''}`.trim();

      if (make && makeLabel.toLowerCase().includes(q) && !seen.has(makeLabel)) {
        seen.add(makeLabel);
        results.push({ label: makeLabel, type: 'make' });
      }
      if (makeModelLabel && makeModelLabel.toLowerCase().includes(q) && !seen.has(makeModelLabel)) {
        seen.add(makeModelLabel);
        results.push({ label: makeModelLabel, type: 'model' });
      }
      if (fullLabel && fullLabel.toLowerCase().includes(q) && !seen.has(fullLabel)) {
        seen.add(fullLabel);
        results.push({ label: fullLabel, type: 'vehicle' });
      }
    });

    return results.slice(0, 8);
  }, [value, vehicles]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setShowSuggestions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      (e.target as HTMLInputElement).blur();
      onSearch?.();
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleSelect = (label: string) => {
    onChange(label);
    setShowSuggestions(false);
    onSearch?.();
  };

  const handleSearchBtn = () => {
    setShowSuggestions(false);
    onSearch?.();
  };

  const isSm = size === 'sm';
  const py = isSm ? 'py-1.5' : 'py-2.5';
  const textSz = isSm ? 'text-xs' : 'text-sm';
  const iconSz = isSm ? 'text-sm' : 'text-base';

  const defaultWrapperStyle: React.CSSProperties = {
    background: '#F7F5F0',
    border: '1.5px solid #E5E7EB',
    ...wrapperStyle,
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center">
        {/* Input */}
        <div
          className={`relative flex-1 flex items-center ${showButton ? 'rounded-l-lg' : 'rounded-lg'}`}
          style={defaultWrapperStyle}
        >
          <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 ${iconSz}`} />
          <input
            type="text"
            value={value}
            onChange={e => { onChange(e.target.value); setShowSuggestions(true); }}
            onFocus={() => { if (value.trim()) setShowSuggestions(true); }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`w-full pl-8 pr-8 ${py} bg-transparent text-[#1A2332] placeholder-slate-400 ${textSz} focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:ring-inset ${inputClassName}`}
            style={inputStyle}
            aria-label={placeholder}
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
          />
          {value && (
            <button
              onClick={() => { onChange(''); setShowSuggestions(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
              aria-label="Clear search"
              type="button"
            >
              <i className={`ri-close-line ${iconSz}`} />
            </button>
          )}
        </div>

        {/* Optional search button */}
        {showButton && (
          <button
            onClick={handleSearchBtn}
            type="button"
            className={`flex-shrink-0 flex items-center justify-center ${py} px-3 bg-[#E8B84B] hover:bg-[#d4a43a] active:bg-[#c09030] text-[#1A2332] rounded-r-lg border border-[#E8B84B] transition-colors cursor-pointer`}
            aria-label="Search inventory"
          >
            <i className={`ri-search-line ${iconSz} font-bold`} />
          </button>
        )}
      </div>

      {/* Autocomplete dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg z-[9999] overflow-hidden"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
          role="listbox"
          aria-label="Inventory suggestions"
        >
          {suggestions.map(s => (
            <button
              key={s.label}
              type="button"
              role="option"
              aria-selected={false}
              onMouseDown={e => { e.preventDefault(); handleSelect(s.label); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[#FFFBF0] transition-colors cursor-pointer border-b border-slate-100 last:border-0"
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i
                  className={`${
                    s.type === 'make'
                      ? 'ri-car-line'
                      : s.type === 'model'
                      ? 'ri-car-washing-line'
                      : 'ri-search-line'
                  } text-slate-400 text-sm`}
                />
              </div>
              <span className="text-sm text-[#1A2332] flex-1 truncate">{s.label}</span>
              <span className="text-[10px] text-slate-400 capitalize flex-shrink-0">{s.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
